import { Box, Skeleton } from '@chakra-ui/react';
import { gridConfig, RecommendedMarquee } from '@time-line/components/common';
import type { RefObject } from 'react';
import { useRef } from 'react';
import {
  SnapList,
  useDragToScroll,
  useVisibleElements,
} from 'react-snaplist-carousel';
import type { ProductMedia } from 'types/cms/pages/ProductPage';

import {
  PRODUCT_GALLERY_RATIO,
  PRODUCT_GALLERY_RATIO_DECIMAL,
} from '../constants';
import type { ProductGalleryProps } from '../types';
import { ProductGalleryMobileInsetLayout as InsetLayout } from './ProductGalleryMobileInsetLayout';
import { ProductGalleryMobileItemWrapper as ItemWrapper } from './ProductGalleryMobileItemWrapper';
import { ProductGalleryMobileMediaItem as MediaItem } from './ProductGalleryMobileMediaItem';

const { borderWidthClamp } = gridConfig;

type GroupedMedia = ([ProductMedia, ProductMedia] | [ProductMedia])[];

export function ProductGalleryMobile({
  media,
  title,
  isDoctorRecommended,
  isLoading = false,
  discountBadge,
}: ProductGalleryProps) {
  const snapList = useRef<HTMLDivElement>(null);
  const visible = useVisibleElements<number>(
    { debounce: 10, ref: snapList as RefObject<HTMLDivElement> },
    ([element]) => element,
  );

  useDragToScroll({
    ref: snapList as RefObject<HTMLDivElement>,
    disabled: false,
  });

  const groupedMedia = media.reduce<GroupedMedia>((acc, item, index) => {
    if (index === 0) {
      acc.push([item]);
    } else if (index === 1) {
      acc[acc.length - 1].push(item);
    } else {
      acc.push([item]);
    }
    return acc;
  }, []);

  const hasOneItem = groupedMedia.length === 1;

  const containerProps = hasOneItem
    ? { mx: borderWidthClamp }
    : {
        as: SnapList,
        direction: 'horizontal',
        sx: {
          scrollbarWidth: 'none',
          '&::webkit-scrollbar': {
            display: 'none',
          },
          '&:before': {
            content: '""',
            flex: '0 0 1px',
          },
        },
      };

  const getItem = (
    group: [ProductMedia, ProductMedia] | [ProductMedia],
    i: number,
  ) => {
    if (isLoading) return <Skeleton />;

    return group.length === 2 ? (
      <InsetLayout
        media={group}
        title={title}
        visibleIndex={visible}
        aspectRatio={PRODUCT_GALLERY_RATIO}
      />
    ) : (
      <MediaItem
        galleryItem={group[0]}
        index={i}
        title={title}
        visibleIndex={visible}
        aspectRatio={PRODUCT_GALLERY_RATIO}
      />
    );
  };

  return (
    <Box
      position="relative"
      width="100%"
      maxW="100%"
      overflow="hidden"
      borderRadius="base"
      role="group"
    >
      <Box
        ref={snapList}
        tabIndex={0}
        role="region"
        aria-label={`Gallery: ${title}`}
        overflowY="hidden"
        overflowX="auto"
        display="flex"
        gap={borderWidthClamp}
        {...containerProps}
      >
        {groupedMedia.map((group, i) => {
          const isFirstThumbnail = i === 0;

          return (
            <ItemWrapper
              key={`item-${i}`}
              aspectRatio={PRODUCT_GALLERY_RATIO_DECIMAL}
              hasOneItem={hasOneItem}
              discountBadge={
                !isLoading && isFirstThumbnail ? discountBadge : null
              }
            >
              {getItem(group, i)}
            </ItemWrapper>
          );
        })}
      </Box>
      {isDoctorRecommended && <RecommendedMarquee pt="3xs" hasFade={false} />}
    </Box>
  );
}
